/**
 * Square card form (first PaymentFormAdapter, tracker item 38): wraps the
 * Square Web Payments SDK. Everything Square-specific stays in this folder.
 *
 * - Script (verified against Square's docs 2026-10-16), loaded lazily in load():
 *   sandbox https://sandbox.web.squarecdn.com/v1/square.js,
 *   production https://web.squarecdn.com/v1/square.js (from clientConfig.environment).
 * - window.Square.payments(applicationId, locationId) -> payments.card() ->
 *   card.attach(element) -> card.tokenize(verificationDetails?) giving
 *   { status: 'OK', token } or { status, errors: [{ message }] } -> card.destroy().
 * - clientConfig keys used: applicationId, locationId, environment (from the
 *   backend pay-config / connection; all non-secret).
 */
import { PaymentClientConfig } from '../../../../api/types';
import { PaymentFormAdapter, PaymentFormError, TokenizeDetails, loadScriptOnce } from '../paymentFormAdapter';

export const SQUARE_PROVIDER = 'SQUARE';
export const SQUARE_SDK_URLS = {
  SANDBOX: 'https://sandbox.web.squarecdn.com/v1/square.js',
  PRODUCTION: 'https://web.squarecdn.com/v1/square.js',
} as const;

interface SquareTokenResult {
  status: string;
  token?: string;
  errors?: { message?: string }[];
}

interface SquareCard {
  attach(target: string | HTMLElement): Promise<void>;
  tokenize(verificationDetails?: Record<string, unknown>): Promise<SquareTokenResult>;
  destroy(): Promise<boolean> | void;
}

interface SquarePayments {
  card(options?: Record<string, unknown>): Promise<SquareCard>;
}

interface SquareGlobal {
  payments(applicationId: string, locationId: string): SquarePayments;
}

declare global {
  interface Window {
    Square?: SquareGlobal;
  }
}

export class SquarePaymentFormAdapter implements PaymentFormAdapter {
  readonly provider = SQUARE_PROVIDER;
  private payments: SquarePayments | null = null;
  private card: SquareCard | null = null;

  async load(clientConfig: PaymentClientConfig): Promise<void> {
    const { applicationId, locationId, environment } = clientConfig;
    if (!applicationId || !locationId) throw new PaymentFormError('Online payment is not set up correctly. Please contact us.');
    await loadScriptOnce(environment === 'PRODUCTION' ? SQUARE_SDK_URLS.PRODUCTION : SQUARE_SDK_URLS.SANDBOX);
    if (!window.Square) throw new PaymentFormError('The card form could not be loaded. Please try again.');
    try {
      this.payments = window.Square.payments(applicationId, locationId);
    } catch {
      throw new PaymentFormError('The card form could not be started. Please try again later.');
    }
  }

  async render(container: HTMLElement): Promise<void> {
    if (!this.payments) throw new PaymentFormError('The card form is not loaded.');
    this.card = await this.payments.card();
    await this.card.attach(container);
  }

  async tokenize(details: TokenizeDetails = {}): Promise<{ sourceToken: string }> {
    if (!this.card) throw new PaymentFormError('The card form is not ready.');
    const verification =
      details.amount !== undefined
        ? { amount: details.amount, currencyCode: 'USD', intent: details.intent ?? 'CHARGE', customerInitiated: true, sellerKeyedIn: false, billingContact: {} }
        : undefined;
    let result: SquareTokenResult;
    try {
      result = await this.card.tokenize(verification);
    } catch {
      throw new PaymentFormError('The card could not be checked. Please try again.');
    }
    if (result.status === 'OK' && result.token) return { sourceToken: result.token };
    throw new PaymentFormError(result.errors?.[0]?.message || 'Please check the card details.');
  }

  destroy(): void {
    const card = this.card;
    this.card = null;
    this.payments = null;
    if (card) void Promise.resolve(card.destroy()).catch(() => undefined);
  }
}

export const squareRegistration = { provider: SQUARE_PROVIDER, create: () => new SquarePaymentFormAdapter() };
